import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { getHomesByCategory } from "../lib/homes";
import Enhanced3DHomeCard from "../components/Enhanced3DHomeCard";
import { getFavoritesFromStorage, toggleFavorite } from "../lib/favorites";

const AllHomesPage: React.FC = () => {
  const coreHomes = getHomesByCategory("core");
  const familyHomes = getHomesByCategory("family");
  const kiwianaHomes = getHomesByCategory("kiwiana");

  // Initialize favorites from localStorage
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);

  useEffect(() => {
    // Load favorites from localStorage on component mount
    const storedFavorites = getFavoritesFromStorage();
    setFavoriteIds(storedFavorites);
  }, []);

  const handleFavoriteToggle = (homeId: string) => {
    const result = toggleFavorite(homeId);
    setFavoriteIds(result.favoriteIds);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Helmet>
        <title>
          All Homes - Rolleston Homes | Browse Our Complete Collection
        </title>
        <meta
          name="description"
          content="Browse our complete collection of quality homes across Core, Family, and Kiwiana categories. View all available homes organized by category."
        />
      </Helmet>

      {/* Page Header */}
      <section className="bg-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6">
            All Homes
          </h1>
          <p className="text-lg text-[#334155] max-w-2xl mx-auto">
            Discover our complete collection of quality homes designed for New
            Zealand living. Browse through our three distinct categories below.
          </p>
        </div>
      </section>

      {/* Core Homes Section */}
      <section className="py-20 bg-gradient-to-b from-[#FAFAFA] to-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6 animate-fade-in-up">
              Core Homes
            </h2>
            <p className="text-xl text-[#334155] max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
              Essential living spaces designed for modern lifestyles. Our Core
              Homes collection offers practical, well-designed homes that
              provide excellent value without compromising on quality.
            </p>
          </div>

          {/* Homes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-16">
            {coreHomes.map((home, index) => (
              <Enhanced3DHomeCard
                key={home.id}
                home={home}
                showCategory={false}
                featured={home.isFeatured}
                animationDelay={index * 0.05}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorite={favoriteIds.includes(home.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Family Homes Section - Orange Background */}
      <section className="py-20 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl animate-pulse delay-500"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-white/15 rounded-full blur-lg animate-bounce"></div>
        </div>

        <div className="max-w-[1280px] mx-auto px-6 md:px-8 relative z-10">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6 animate-fade-in-up">
              Family Homes
            </h2>
            <p className="text-xl text-[#334155] max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
              Spacious designs perfect for growing families. Our Family Homes
              feature multiple living areas, generous bedrooms, and thoughtful
              layouts that adapt to your family's changing needs.
            </p>
          </div>

          {/* Homes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-16">
            {familyHomes.map((home, index) => (
              <Enhanced3DHomeCard
                key={home.id}
                home={home}
                showCategory={false}
                featured={home.isFeatured}
                animationDelay={index * 0.05}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorite={favoriteIds.includes(home.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Kiwiana Homes Section */}
      <section className="py-20 bg-gradient-to-r from-[#FAFAFA] to-white">
        <div className="max-w-[1280px] mx-auto px-6 md:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1F2937] mb-6 animate-fade-in-up">
              Kiwiana Homes
            </h2>
            <p className="text-xl text-[#334155] max-w-3xl mx-auto mb-10 leading-relaxed animate-fade-in-up delay-200">
              Celebrating New Zealand's unique architectural heritage. Our
              Kiwiana collection combines traditional Kiwi design elements with
              modern comfort and functionality.
            </p>
          </div>

          {/* Homes Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 mb-16">
            {kiwianaHomes.map((home, index) => (
              <Enhanced3DHomeCard
                key={home.id}
                home={home}
                showCategory={false}
                featured={home.isFeatured}
                animationDelay={index * 0.05}
                onFavoriteToggle={handleFavoriteToggle}
                isFavorite={favoriteIds.includes(home.id)}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AllHomesPage;
